import React, { useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { joApi, joErr } from '@/services/processingJoApi';
import { CheckBadge } from './ReturnChecksTable';
import { fmtN } from './JoBits';

/** Processing Inward: a returned job-order bundle doesn't match what was sent — wait for (or give) a supervisor's approval. */
export const ReturnCheckDialog = ({ data, onClose, onApproved }) => {
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState('');
  const ck = data?.check;
  const decide = async (approve) => {
    setBusy(approve ? 'approve' : 'reject');
    try {
      await joApi.decideCheck(ck.check_id, approve, remarks.trim());
      setRemarks('');
      if (approve) { toast.success(`${ck.check_id} approved — saving the bundle`); onApproved(); } else { toast.success(`${ck.check_id} rejected`); onClose(); }
    } catch (e) { toast.error(joErr(e, 'Could not save')); } finally { setBusy(''); }
  };
  const recheck = async () => {
    setBusy('check');
    try {
      const r = await joApi.returnCheck(ck.check_id);
      if (r.status === 'APPROVED') { toast.success(`${ck.check_id} approved by ${r.decided_by_name} — saving the bundle`); onApproved(); }
      else toast.info(r.status === 'REJECTED' ? `Rejected by ${r.decided_by_name} — correct the count / sort no / colour` : 'Still waiting for a supervisor');
    } catch (e) { toast.error(joErr(e, 'Could not check')); } finally { setBusy(''); }
  };
  return (
    <Dialog open={!!ck} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="return-check-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" />Doesn't match job order {ck?.jo_id}</DialogTitle>
          <DialogDescription>Bundle {ck?.bundle_code} can't be received until a supervisor approves it.</DialogDescription>
        </DialogHeader>
        {ck && <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2"><span className="font-mono text-xs" data-testid="return-check-id">{ck.check_id}</span><CheckBadge status={ck.status} testId="return-check-status" /></div>
          <ul className="space-y-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900" data-testid="return-check-issues">
            {ck.issues.map((i) => <li key={i.type}>• {i.text}</li>)}</ul>
          <p className="text-xs text-gray-600">Sent: {fmtN(ck.sent_pcs)} pcs · sort {ck.sent_sort_no}{ck.sent_colour ? ` · ${ck.sent_colour}` : ''}{ck.jobber_name ? ` · ${ck.jobber_name}` : ''} · {ck.transfer_id}</p>
          {data.can_approve && <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Supervisor remark (required)" data-testid="return-check-remarks" />}
          {!data.can_approve && <p className="text-xs text-gray-600" data-testid="return-check-ask">Ask a supervisor to approve it in Inhouse → Job Order Return Checks, then press Check again.</p>}
        </div>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="return-check-close">Close</Button>
          {data?.can_approve ? <>
            {ck?.status === 'PENDING' && <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" disabled={!!busy || !remarks.trim()} onClick={() => decide(false)} data-testid="return-check-reject">
              {busy === 'reject' && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Reject</Button>}
            <Button className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={!!busy || !remarks.trim()} onClick={() => decide(true)} data-testid="return-check-approve">
              {busy === 'approve' && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Approve &amp; save</Button>
          </> : <Button onClick={recheck} disabled={!!busy} className="bg-[#1A1F2E] text-white hover:bg-[#2A3142]" data-testid="return-check-recheck">
            {busy === 'check' && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Check again</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReturnCheckDialog;
